<template>
  <div id="app" class="min-h-screen bg-gray-50 dark:bg-gray-900 transition-colors duration-200">
    <!-- Loading Global -->
    <Transition name="fade">
      <div 
        v-if="isLoading" 
        class="fixed inset-0 z-50 flex items-center justify-center bg-white/80 dark:bg-gray-900/80 backdrop-blur-sm"
      >
        <div class="flex flex-col items-center space-y-4">
          <div class="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
          <p class="text-gray-600 dark:text-gray-300 font-medium">{{ $t('common.loading') }}</p>
        </div>
      </div>
    </Transition>

    <!-- Aplicação Principal -->
    <div v-if="!isLoading" class="flex h-screen overflow-hidden">
      <!-- Sidebar -->
      <Transition name="slide-x">
        <Sidebar 
          :is-open="showMobileSidebar"
          v-if="isAuthenticated && showSidebar" 
          :is-collapsed="!sidebarOpen" 
          @toggle-collapse="toggleSidebar"
          class="hidden lg:flex"
        />
      </Transition>

      <!-- Mobile Sidebar Overlay -->
      <Transition name="fade">
        <div 
          v-if="isAuthenticated && showMobileSidebar" 
          class="fixed inset-0 z-40 lg:hidden"
          @click="closeMobileSidebar"
        >
          <div class="absolute inset-0 bg-gray-600 opacity-75"></div>
          <Sidebar
            :is-open="showMobileSidebar"
            :is-mobile="true"
            @close="closeMobileSidebar"
            class="relative z-50"
          />
        </div>
      </Transition>

      <!-- Conteúdo Principal -->
      <div class="flex-1 flex flex-col overflow-hidden">
        <!-- Header -->
        <Header
          :sidebar-open="sidebarOpen"
          v-if="isAuthenticated"
          @toggle-sidebar="toggleMobileSidebar"
          @toggle-theme="toggleTheme"
        />

        <!-- Main Content -->
        <main class="flex-1 overflow-auto">
          <RouterView v-slot="{ Component, route }">
            <Transition 
              :name="(route.meta.transition as string) || 'fade'"
              mode="out-in"
              appear
            >
              <Suspense>
                <component :is="Component" :key="route.path" />
                <template #fallback>
                  <div class="flex items-center justify-center h-64">
                    <div class="animate-pulse flex space-x-4">
                      <div class="rounded-full bg-gray-300 h-10 w-10"></div>
                      <div class="flex-1 space-y-2 py-1">
                        <div class="h-4 bg-gray-300 rounded w-3/4"></div>
                        <div class="h-4 bg-gray-300 rounded w-1/2"></div>
                      </div>
                    </div>
                  </div>
                </template>
              </Suspense>
            </Transition>
          </RouterView>
        </main>

        <!-- Footer -->
        <Footer v-if="isAuthenticated && showFooter" />
      </div>
    </div>

    <!-- Notificações Toast -->
    <div id="toast-container"></div>

    <!-- Modal Global -->
    <Teleport to="body">
      <GlobalModal />
    </Teleport>

    <!-- Confirmação Global -->
    <Teleport to="body">
      <GlobalConfirm />
    </Teleport>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, watch } from 'vue'
import { useRoute } from 'vue-router' // Removed useRouter as it's unused
import { storeToRefs } from 'pinia'
import { useAuthStore } from '@/stores/auth'
import { useThemeStore } from '@/stores/theme'
import { useAppStore } from '@/stores/app'

// Componentes
import Sidebar from '@/components/layout/Sidebar.vue'
import Header from '@/components/layout/Header.vue'
import Footer from '@/components/layout/Footer.vue'
import GlobalModal from '@/components/ui/GlobalModal.vue'
import GlobalConfirm from '@/components/ui/GlobalConfirm.vue'

// const router = useRouter()
const route = useRoute()

// Stores
const authStore = useAuthStore()
const themeStore = useThemeStore()
const appStore = useAppStore()

const { isAuthenticated } = storeToRefs(authStore)
const { isDarkMode } = storeToRefs(themeStore)
const { isLoading, sidebarOpen } = storeToRefs(appStore) // ✅ Usar sidebarOpen em vez de isSidebarCollapsed

// Estado local para mobile sidebar
const showMobileSidebar = ref(false) // ✅ Criar estado local

// Estado local
const showSidebar = computed(() => {
  const hiddenRoutes = ['login', 'register', 'forgot-password', 'reset-password']
  return !hiddenRoutes.includes(route.name as string)
})

const showFooter = computed(() => {
  const hiddenRoutes = ['login', 'register']
  return !hiddenRoutes.includes(route.name as string)
})

// Métodos
const toggleSidebar = () => {
  appStore.toggleSidebar()
}

const toggleMobileSidebar = () => {
  showMobileSidebar.value = !showMobileSidebar.value // ✅ Usar estado local
}

const closeMobileSidebar = () => {
  showMobileSidebar.value = false // ✅ Usar estado local
}

const toggleTheme = () => {
  themeStore.toggleTheme()
}

// Watchers
watch(isDarkMode, (newValue) => {
  if (newValue) {
    document.documentElement.classList.add('dark')
  } else {
    document.documentElement.classList.remove('dark')
  }
}, { immediate: true })

watch(route, () => {
  // Fechar sidebar mobile ao navegar
  if (showMobileSidebar.value) { // ✅ Usar estado local
    closeMobileSidebar()
  }
})

// Lifecycle
onMounted(async () => {
  // Verificar autenticação
  await authStore.checkAuth()
  
  // Aplicar tema salvo
  themeStore.initializeTheme()
  
  // Configurar responsividade
  const handleResize = () => {
    if (window.innerWidth >= 1024) {
      closeMobileSidebar() // ✅ Usar função local
    }
  }
  
  window.addEventListener('resize', handleResize)
  
  // Cleanup
  return () => {
    window.removeEventListener('resize', handleResize)
  }
})
</script>

<style>
/* Transições */
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.3s ease;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}

.slide-x-enter-active,
.slide-x-leave-active {
  transition: transform 0.3s ease;
}

.slide-x-enter-from {
  transform: translateX(-100%);
}

.slide-x-leave-to {
  transform: translateX(-100%);
}

.slide-up-enter-active,
.slide-up-leave-active {
  transition: all 0.3s ease;
}

.slide-up-enter-from {
  transform: translateY(20px);
  opacity: 0;
}

.slide-up-leave-to {
  transform: translateY(-20px);
  opacity: 0;
}

/* Scrollbar personalizada */
::-webkit-scrollbar {
  width: 6px;
  height: 6px;
}

::-webkit-scrollbar-track {
  background: #f1f5f9;
}

::-webkit-scrollbar-thumb {
  background: #cbd5e1;
  border-radius: 3px;
}

::-webkit-scrollbar-thumb:hover {
  background: #94a3b8;
}

.dark ::-webkit-scrollbar-track {
  background: #1e293b;
}

.dark ::-webkit-scrollbar-thumb {
  background: #475569;
}

.dark ::-webkit-scrollbar-thumb:hover {
  background: #64748b;
}

/* Animações personalizadas */
@keyframes pulse-soft {
  0%, 100% {
    opacity: 1;
  }
  50% {
    opacity: 0.8;
  }
}

.animate-pulse-soft {
  animation: pulse-soft 2s cubic-bezier(0.4, 0, 0.6, 1) infinite;
}
</style>